import React, { useState, useEffect } from 'react';
import { FiArrowLeft, FiMail, FiPhone, FiMapPin } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';

const ContactUs = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => {
      setFormData({ name: '', email: '', subject: '', message: '' });
      setSubmitted(false);
    }, 3000);
  };

  return (
    <div className="bg-brand-dark min-h-screen text-white">
      <SEO 
        title="Contact Us - Christian Videos"
        description="Get in touch with Christian Videos. We'd love to hear from you!"
        url="https://christianvideos.in/contact-us"
      />
      {/* Back Button */}
      <div className="p-4 sm:p-6 flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 hover:text-gray-300 transition-colors text-sm sm:text-base"
        >
          <FiArrowLeft size={20} className="sm:w-6 sm:h-6" />
          <span>Back</span>
        </button>
      </div>

      {/* Content */}
      <div className="px-4 sm:px-6 md:px-8 lg:pl-20 lg:pr-8 py-8 sm:py-12">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2 text-gradient-accent">
          Contact Us
        </h1>
        <p className="text-gray-400 mb-12">We'd love to hear from you. Get in touch with us today.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {/* Contact Info */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <FiMail size={24} className="text-gradient-accent" />
              <h3 className="text-lg font-semibold">Email</h3>
            </div>
            <p className="text-gray-400">support@christianvideos.in</p>
            <p className="text-gray-500 text-sm mt-2">We'll respond within 24 hours</p>
          </div>

          <div className="bg-gray-800 p-6 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <FiPhone size={24} className="text-gradient-accent" />
              <h3 className="text-lg font-semibold">Phone</h3>
            </div>
            <p className="text-gray-400">+1 (555) 123-4567</p>
            <p className="text-gray-500 text-sm mt-2">Mon - Fri, 9AM - 6PM EST</p>
          </div>

          <div className="bg-gray-800 p-6 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <FiMapPin size={24} className="text-gradient-accent" />
              <h3 className="text-lg font-semibold">Website</h3>
            </div>
            <p className="text-gray-400">https://christianvideos.in</p>
            <p className="text-gray-500 text-sm mt-2">Visit us online 24/7</p>
          </div>
        </div>

        {/* Contact Form */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-gray-800 p-8 rounded-lg">
            <h2 className="text-2xl font-semibold mb-6">Send us a Message</h2>
            
            {submitted && (
              <div className="mb-6 p-4 bg-green-600/20 border border-green-600 rounded-lg text-green-400">
                Thank you for your message! We'll get back to you soon.
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-gradient-accent transition-colors"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-gradient-accent transition-colors"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-gradient-accent transition-colors"
                  placeholder="What is this about?"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-gradient-accent transition-colors resize-none"
                  placeholder="Your message here..."
                />
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-accent hover:opacity-90 text-white font-semibold py-3 rounded-lg transition-opacity"
              >
                Send Message
              </button>
            </form>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-2xl mx-auto mt-12">
          <h2 className="text-2xl font-semibold mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <div className="bg-gray-800 p-6 rounded-lg">
              <h3 className="font-semibold mb-2 text-white">How do I report technical issues?</h3>
              <p className="text-gray-400">Please email us at support@christianvideos.com with a detailed description of the issue and steps to reproduce it.</p>
            </div>
            <div className="bg-gray-800 p-6 rounded-lg">
              <h3 className="font-semibold mb-2 text-white">Can I request specific content?</h3>
              <p className="text-gray-400">Yes! Send us a message with your content suggestions and we'll review them for addition to our platform.</p>
            </div>
            <div className="bg-gray-800 p-6 rounded-lg">
              <h3 className="font-semibold mb-2 text-white">How can I partner with Christian Videos?</h3>
              <p className="text-gray-400">For partnership inquiries, please contact us at partnerships@christianvideos.com.</p>
            </div>
            <div className="bg-gray-800 p-6 rounded-lg">
              <h3 className="font-semibold mb-2 text-white">Is the platform completely free?</h3>
              <p className="text-gray-400">Yes! Christian Videos is completely free to use. We're committed to bringing inspirational content to everyone.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
